import { GenData, Provider, EGenMode } from '@educraft/base';
import {
  PdfGen,
  PageGen,
  PdfRect,
  PdfSize,
  PdfUnit,
  PdfBrush,
  ETextAlign,
  PdfFont,
  PdfPoint,
} from '../pdf/pdf';
import { GUtil } from '../pdf/gutil';
import { IColorSchema, IColorSchemaSymbol } from './colorSchema';
import { IExercise, IExerciseSymbol, ExerciseData } from './exercise';
import { ExeProject, ExeItem, ExeResults, EExeItemType } from './exeProject';
import { ColorSchemaBlack } from './colorSchemaBlack';

interface RenderVars {
  counter: number;
}

export async function generateExe(
  pdf: PdfGen,
  project: ExeProject,
  data: GenData,
  provider: Provider
): Promise<void> {
  const vars: RenderVars = { counter: 1 };
  const results = new ExeResults();

  let items: ExeItem[] = [];

  for (let i = 0; i <= project.items.length; i++) {
    if (i === project.items.length || project.items[i].type === EExeItemType.NewPage) {
      renderPage(pdf, project, vars, results, items, data, provider);
      items = [];
    } else {
      items.push(project.items[i]);
    }
  }

  if (project.options.renderResults) {
    renderResults(pdf, project, results, data);
  }
}

function getMargin(project: ExeProject): number {
  return PdfUnit.FromMillimeters(project.options.margin ?? 20);
}

function renderPage(
  pdf: PdfGen,
  project: ExeProject,
  vars: RenderVars,
  results: ExeResults,
  items: ExeItem[],
  data: GenData,
  provider: Provider
): void {
  if (items.length === 0) return;

  const pageIndex = pdf.gs.length;
  const page = pdf.addPage();

  // Resolve color schema
  let colorSchema: IColorSchema;
  const schemaName = project.options.colorSchema;
  if (provider.has(IColorSchemaSymbol, schemaName)) {
    colorSchema = provider.getOne<IColorSchema>(IColorSchemaSymbol, schemaName);
  } else {
    colorSchema = new ColorSchemaBlack();
  }

  const margin = getMargin(project);
  const pageRect = new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height);
  const totalHeight = pageRect.height - 2.5 * margin;
  const inner = new PdfRect(margin, margin, pageRect.width - 2 * margin, totalHeight);

  colorSchema.drawPage(page, pageRect, inner, pdf.gs.length);

  // Resolve exercises
  const exercises: IExercise[] = items.map((item) =>
    provider.getOne<IExercise>(EGenMode.Exercise, item.item)
  );

  // Calculate sizes: fixed-height items keep their height, flexible share remaining
  const sizes = exercises.map((ex) => ex.fixedHeight);
  const flexibleIndices: number[] = [];
  for (let i = 0; i < sizes.length; i++) {
    if (sizes[i] === 0) flexibleIndices.push(i);
  }

  if (flexibleIndices.length > 0) {
    const sumWeights = flexibleIndices.reduce((sum, idx) => sum + items[idx].size, 0);
    const fixedHeight = sizes.reduce((sum, s) => sum + s, 0);
    const remainingHeight = totalHeight - fixedHeight;

    for (const idx of flexibleIndices) {
      sizes[idx] = (items[idx].size * remainingHeight) / sumWeights;
    }
  }

  // Render each exercise
  let top = margin;
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    const rect = new PdfRect(margin, top, PdfSize.A4.width - 2 * margin, sizes[index]);

    let exerciseData: ExerciseData;
    if (exercises[index].noWindow) {
      exerciseData = {
        page,
        rect,
        pageIndex,
        exerciseIndex: index,
        data,
        colorSchema,
        item,
        results,
        provider,
      };
    } else {
      const content = colorSchema.calcContent(rect);
      colorSchema.drawWindow(page, rect, index, `${vars.counter++}`);
      exerciseData = {
        page,
        rect: content,
        pageIndex,
        exerciseIndex: index,
        data,
        colorSchema,
        item,
        results,
        provider,
      };
    }

    exercises[index].draw(exerciseData);
    top += sizes[index];
  }
}

function renderResults(pdf: PdfGen, project: ExeProject, results: ExeResults, data: GenData): void {
  if (results.exercises.size === 0) return;

  const margin = getMargin(project);
  const lineHeight = PdfUnit.FromMillimeters(8);
  let page = pdf.addPage();
  let top = margin;
  let left = margin;

  for (const [exercise, items] of results.exercises) {
    left = margin;

    const r = new PdfRect(left, top, PdfSize.A4.width - 2 * margin, lineHeight);
    page.drawString(exercise, GUtil.exerciseFont, PdfBrush.Black, null, r, ETextAlign.CenterLeft);
    top += lineHeight;

    for (const result of items) {
      let str = '';
      if (result.key) {
        str += `${result.key} `;
      }
      str += result.value;

      const size = page.measureString(str, GUtil.exerciseFont);

      if (left > margin && left + size.width > PdfSize.A4.width - margin) {
        top += lineHeight;
        left = margin;
      }

      if (top + PdfUnit.FromMillimeters(20) > PdfSize.A4.height - margin) {
        page = pdf.addPage();
        top = margin;
        left = margin;
      }

      const r2 = new PdfRect(left, top, size.width + PdfUnit.FromMillimeters(4), lineHeight);
      page.drawString(str, GUtil.exerciseFont, PdfBrush.Black, null, r2, ETextAlign.CenterLeft);

      left += size.width + PdfUnit.FromMillimeters(4);
    }

    top += lineHeight * 1.8;
  }
}
